// SEP-2 / E0 — EntityFormSheet: a guided, validated, accessible create/edit form rendered as a
// focus-trapped dialog (a bottom sheet on mobile, a centered card on desktop). It is DATA-DRIVEN by a
// category-agnostic field schema (EntityField[]) so Shop (product / item / contact / deal) AND Booking
// (service) build their guided forms from the same primitive — no bespoke form per category.
//
// Honesty + a11y (WCAG 2.2): every field has a visible <label>; required + number/price fields are
// validated client-side; on a failed submit focus moves to the first invalid field and the error is
// announced via aria-describedby + role="alert"; the submit button never reports success before the
// caller's onSubmit resolves ok (the caller does the real server write and can return per-field server
// errors). Esc closes; Tab is trapped; role="dialog" + aria-modal.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { X, Loader2 } from 'lucide-preact';
import { t } from '../lib/i18n';
import { EntityMediaField } from './EntityMediaField';
import type { MediaValue } from './EntityMediaField';

/** The kinds of field the guided form can render. All category-agnostic. */
export type EntityFieldType = 'text' | 'number' | 'price' | 'textarea' | 'select' | 'media' | 'chips';

/** One field in a guided entity form. */
export interface EntityField {
  /** the key written into the entity row's data payload. */
  name: string;
  /** i18n key for the field's visible label. */
  labelKey: string;
  type: EntityFieldType;
  required?: boolean;
  /** i18n key for placeholder / helper text. */
  placeholderKey?: string;
  helpKey?: string;
  /** number/price bounds. */
  min?: number;
  max?: number;
  step?: number;
  /** options for a `select` field (values are raw; labels are i18n keys). */
  options?: readonly { value: string; labelKey: string }[];
  /** for `media`: whether alt text is required (default true). */
  altRequired?: boolean;
}

/** A single field's value. Media carries {url,alt}; chips carry a string[]. */
export type FieldValue = string | number | boolean | MediaValue | string[] | undefined;

/** What the caller's onSubmit returns. `ok:false` may carry per-field server errors + a banner key. */
export interface FormSubmitResult {
  ok: boolean;
  fieldErrors?: Record<string, string>; // name → already-resolved message (server truth)
  errorKey?: string;                     // i18n key for a top-of-form banner
}

export interface EntityFormSheetProps {
  open: boolean;
  /** i18n key for the dialog title (e.g. "Add product" / "Edit product"). */
  titleKey: string;
  fields: readonly EntityField[];
  /** owner app id (passed to media fields). */
  appId: string;
  /** initial values (edit prefill); empty for create. */
  initialValues?: Record<string, FieldValue>;
  /** i18n key for the submit button (default own.save). */
  submitLabelKey?: string;
  /** does the REAL server write; returns ok + optional field/banner errors. NEVER a fake success. */
  onSubmit: (values: Record<string, FieldValue>) => Promise<FormSubmitResult>;
  onClose: () => void;
}

function isBlank(v: FieldValue): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return !('url' in v) || !(v as MediaValue).url;
  return false;
}

export function EntityFormSheet({
  open, titleKey, fields, appId, initialValues, submitLabelKey = 'own.save', onSubmit, onClose,
}: EntityFormSheetProps): JSX.Element | null {
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState('');
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLElement | null>(null);

  // Reset local state each time the sheet opens (fresh prefill, no stale errors).
  useEffect(() => {
    if (!open) return;
    setValues({ ...(initialValues ?? {}) });
    setErrors({});
    setBanner('');
    // focus the first field shortly after mount
    setTimeout(() => firstFieldRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus-trap + Esc.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Tab') {
        const f = dialogRef.current?.querySelectorAll<HTMLElement>('input,select,textarea,button,[tabindex]:not([tabindex="-1"])');
        if (!f || f.length === 0) return;
        const list = [...f].filter((el) => !(el as HTMLButtonElement).disabled);
        const first = list[0];
        const last = list[list.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const titleId = useMemo(() => `efs-title-${Math.random().toString(36).slice(2, 8)}`, []);

  if (!open) return null;

  function setField(name: string, v: FieldValue): void {
    setValues((cur) => ({ ...cur, [name]: v }));
    setErrors((cur) => (cur[name] ? { ...cur, [name]: '' } : cur));
  }

  /** Client-side validation → { name: messageKey }. Required + number parseability + media alt. */
  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    for (const f of fields) {
      const v = values[f.name];
      if (f.required && isBlank(v)) { next[f.name] = t('own.required'); continue; }
      if ((f.type === 'number' || f.type === 'price') && !isBlank(v)) {
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isNaN(n)) next[f.name] = t('own.invalidNumber');
      }
      if (f.type === 'media') {
        const mv = (v as MediaValue) || { url: null };
        const altReq = f.altRequired !== false;
        if (mv.url && altReq && !(mv.alt || '').trim()) next[f.name] = t('own.mediaAltRequired');
      }
    }
    return next;
  }

  async function submit(e: Event): Promise<void> {
    e.preventDefault();
    if (saving) return;
    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      setBanner(t('own.fixErrors'));
      // focus the first invalid field
      const firstBad = fields.find((f) => v[f.name]);
      if (firstBad) dialogRef.current?.querySelector<HTMLElement>(`[data-field="${firstBad.name}"]`)?.focus();
      return;
    }
    setSaving(true);
    setBanner('');
    try {
      const res = await onSubmit(values);
      if (res.ok) { onClose(); return; }
      // honest failure — surface server field errors + banner, DO NOT close
      setErrors(res.fieldErrors ?? {});
      setBanner(res.errorKey ? t(res.errorKey) : t('own.saveFailed'));
    } catch {
      setBanner(t('own.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,8,14,0.55)', zIndex: 998 }} aria-hidden="true" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={sheetStyle}
      >
        <div className="pf-row" style={{ marginBottom: 4 }}>
          <span id={titleId} style={{ fontSize: 16, fontWeight: 800, color: 'var(--fg)' }}>{t(titleKey)}</span>
          <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost" aria-label={t('own.close')} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {banner && <p role="alert" style={{ margin: 0, fontSize: 12.5, color: 'var(--color-danger)', background: 'var(--err-soft)', padding: '8px 10px', borderRadius: 8 }}>{banner}</p>}

        <form onSubmit={(e) => void submit(e)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} noValidate>
          {fields.map((f, i) => {
            const err = errors[f.name];
            const errId = `${f.name}-err`;
            const hintId = `${f.name}-hint`;
            const describedBy = err ? errId : (f.helpKey ? hintId : undefined);
            const fieldId = `field-${f.name}`;
            const setRef = (el: HTMLElement | null): void => { if (i === 0) firstFieldRef.current = el; };

            if (f.type === 'media') {
              return (
                <div key={f.name} data-field={f.name}>
                  <EntityMediaField
                    appId={appId}
                    labelKey={f.labelKey}
                    value={(values[f.name] as MediaValue) || { url: null }}
                    altRequired={f.altRequired !== false}
                    altError={!!err}
                    onChange={(mv) => setField(f.name, mv)}
                  />
                </div>
              );
            }

            return (
              <label key={f.name} htmlFor={fieldId} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
                  {t(f.labelKey)}{f.required ? ' *' : ''}
                </span>

                {f.type === 'textarea' ? (
                  <textarea
                    id={fieldId} data-field={f.name} className="pf-input"
                    ref={setRef as (el: HTMLTextAreaElement | null) => void}
                    value={(values[f.name] as string) || ''}
                    required={f.required}
                    aria-required={f.required ? 'true' : 'false'}
                    aria-invalid={err ? 'true' : 'false'}
                    aria-describedby={describedBy}
                    placeholder={f.placeholderKey ? t(f.placeholderKey) : undefined}
                    onInput={(e) => setField(f.name, (e.target as HTMLTextAreaElement).value)}
                    style={{ ...inputStyle, height: 80, padding: 10, resize: 'vertical' }}
                  />
                ) : f.type === 'select' ? (
                  <select
                    id={fieldId} data-field={f.name} className="pf-input"
                    ref={setRef as (el: HTMLSelectElement | null) => void}
                    value={(values[f.name] as string) || ''}
                    required={f.required}
                    aria-required={f.required ? 'true' : 'false'}
                    aria-invalid={err ? 'true' : 'false'}
                    aria-describedby={describedBy}
                    onChange={(e) => setField(f.name, (e.target as HTMLSelectElement).value)}
                    style={inputStyle}
                  >
                    <option value="">{f.placeholderKey ? t(f.placeholderKey) : '—'}</option>
                    {f.options?.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                  </select>
                ) : f.type === 'chips' ? (
                  <input
                    id={fieldId} data-field={f.name} className="pf-input" type="text"
                    ref={setRef as (el: HTMLInputElement | null) => void}
                    value={Array.isArray(values[f.name]) ? (values[f.name] as string[]).join(', ') : ((values[f.name] as string) || '')}
                    aria-invalid={err ? 'true' : 'false'}
                    aria-describedby={describedBy}
                    placeholder={f.placeholderKey ? t(f.placeholderKey) : undefined}
                    onInput={(e) => setField(f.name, (e.target as HTMLInputElement).value.split(',').map((s) => s.trim()).filter(Boolean))}
                    style={inputStyle}
                  />
                ) : (
                  <input
                    id={fieldId} data-field={f.name} className="pf-input"
                    ref={setRef as (el: HTMLInputElement | null) => void}
                    type={f.type === 'number' || f.type === 'price' ? 'number' : 'text'}
                    inputMode={f.type === 'price' ? 'decimal' : undefined}
                    value={values[f.name] === undefined ? '' : String(values[f.name])}
                    required={f.required}
                    aria-required={f.required ? 'true' : 'false'}
                    aria-invalid={err ? 'true' : 'false'}
                    aria-describedby={describedBy}
                    min={f.min} max={f.max} step={f.step ?? (f.type === 'price' ? 0.01 : undefined)}
                    placeholder={f.placeholderKey ? t(f.placeholderKey) : undefined}
                    onInput={(e) => {
                      const raw = (e.target as HTMLInputElement).value;
                      setField(f.name, f.type === 'number' || f.type === 'price' ? (raw === '' ? undefined : Number(raw)) : raw);
                    }}
                    style={inputStyle}
                  />
                )}

                {err
                  ? <span id={errId} role="alert" style={{ fontSize: 12, color: 'var(--color-danger)' }}>{err}</span>
                  : f.helpKey ? <span id={hintId} style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t(f.helpKey)}</span> : null}
              </label>
            );
          })}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 2 }}>
            <button type="button" className="pf-btn pf-btn--sm" onClick={onClose} disabled={saving}>{t('own.cancel')}</button>
            <button type="submit" className="pf-btn pf-btn--sm pf-btn--soft" disabled={saving}>
              {saving ? <><Loader2 size={13} className="pf-spin" /> {t('own.saving')}</> : t(submitLabelKey)}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

const inputStyle: JSX.CSSProperties = {
  height: 40, padding: '0 12px', fontSize: 14, borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--bg-el)', color: 'var(--fg)', width: '100%', boxSizing: 'border-box',
};

const sheetStyle: JSX.CSSProperties = {
  position: 'fixed', zIndex: 999, insetInlineStart: '50%', top: '50%', transform: 'translate(-50%, -50%)',
  width: 'min(520px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
  padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 24px 64px rgba(0,0,0,0.32)',
};
